import React, { useEffect, useRef, useState } from 'react'

import DynamicHighchart from '~/components/atoms/DynamicHighchart'
import { ComponentTheme } from '~/components/themes'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'

import styles from './ArtworkData.module.css'

type DataType = {
  date: number
  value: number
}

export interface ArtworkDataChartProps {
  data: DataType[]
  theme?: ComponentTheme
  enableTooltips?: boolean
}

export default function ArtworkDataChart({
  data,
  theme = ComponentTheme.secondary,
  enableTooltips = true,
}: ArtworkDataChartProps) {
  const [seriesData, setSeriesData] = useState<any | null>(null)
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMedium})`)

  const chartComponentRef = useRef<any>(null)

  const chartColors = [
    '#4dc3f8',
    '#6068d6',
    '#ca6bda',
    '#33cf74',
    '#fe6b35',
    '#f8d84d',
    '#4df8d8',
    '#f84d6d',
  ]

  const seraliseData = () => {
    const newChartData: any[] = []

    if (data) {
      const sortData = structuredClone(data)
      let c = 0
      sortData.forEach((item: any) => {
        const data = item.data
        const hasSingleDataPoint = data && data.length === 1
        const newItem: any = {
          showInLegend: false,
          data: data && data,
          color: chartColors[c],
          visible: item.isVisible,
        }

        if (hasSingleDataPoint) {
          newItem.marker = {
            enabled: true,
            radius: 6,
          }
        }

        c++
        newChartData.push(newItem)
      })
      setSeriesData(newChartData)
    }
  }

  useEffect(() => seraliseData(), [data])

  useEffect(() => {
    return () => {
      setSeriesData(null)
    }
  }, [])

  function getChartColors(theme: ComponentTheme) {
    switch (theme) {
      case ComponentTheme.primary:
        return {
          bg: '#fff',
        }
      case ComponentTheme.secondary:
        return {
          bg: '#fafafa',
        }

      case ComponentTheme.black:
        return {
          bg: '#0a0a0a',
        }
      default:
        return {
          bg: '#fff',
        }
    }
  }

  const options = {
    title: {
      text: '',
      styledMode: true,
    },
    navigator: {
      enabled: true,
    },
    rangeSelector: {
      selected: 1,
    },
    xAxis: {
      title: { text: '' },
      uniqueNames: true,
      gridLineColor: '#EEE',
      startOnTick: false,
      endOnTick: false,
      minorGridLineWidth: 0,
      type: 'datetime',
      labels: {
        style: {
          fontFamily: 'Noir No1',
          fontWeight: 400,
          fontSize: isMobile ? 10 : 14,
        },
      },
      dateTimeLabelFormats: {
        month: {
          main: '%b %Y',
        },
      },
    },
    credits: {
      text: '© MyArtBroker',
    },
    yAxis: {
      title: {
        text: '',
      },
      gridLineColor: '#EEE',
      minorTicks: false,
      startOnTick: false,
      endOnTick: true,
      labels: {
        style: {
          fontFamily: 'Noir No1',
          fontWeight: 400,
          fontSize: isMobile ? 10 : 14,
        },
        formatter: function (this: any): string {
          return `£${this.value.toLocaleString('en-GB')}`
        },
      },
    },
    chart: {
      height: isMobile ? 200 : 380,
      type: 'spline',
      className: styles.highChartContainer,
      backgroundColor: getChartColors(theme)?.bg,
    },
    tooltip: {
      enabled: enableTooltips,
      backgroundColor: '#000',
      borderColor: '#000',
      borderWidth: 0,
      style: {
        color: '#fff',
        whitespace: '1',
        textAlign: 'center',
        fontSize: '12px',
      },
      shadow: false,
      xDateFormat: '%Y-%m-%d',
      formatter: function (this: any): string {
        const { x, y } = this

        // Simple date formatting since we'll load Highcharts dynamically
        const date = new Date(x)
        const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`

        return `${formattedDate} <br /> £${y.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`
      },
    },
    plotOptions: {
      series: {
        allowPointSelect: true,
        marker: {
          enabled: false,
        },
        states: {
          hover: {
            enabled: enableTooltips,
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
    },
    // series: seriesData && cloneDeep(seriesData).map((item: any) => item),
    series: seriesData,
    exporting: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
  }

  return <DynamicHighchart options={options} ref={chartComponentRef} />
}
